import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Users, Grid3x3, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import CalendrierDetailInfoTab from "../components/calendriers/CalendrierDetailInfoTab";
import CalendrierDetailHolidaysTab from "../components/calendriers/CalendrierDetailHolidaysTab";
import CalendrierDetailEmployeesTab from "../components/calendriers/CalendrierDetailEmployeesTab";
import CalendrierDetailCalendarView from "../components/calendriers/CalendrierDetailCalendarView";
import CalendrierDetailAddHolidayDialog from "../components/calendriers/CalendrierDetailAddHolidayDialog";
import CalendrierDetailRemoveDialog from "../components/calendriers/CalendrierDetailRemoveDialog";

const STATUS_CONFIG = {
  DRAFT:    { className: 'bg-gray-500' },
  APPROVED: { className: 'bg-green-500' },
  ARCHIVED: { className: 'bg-orange-500' },
};

const JoursFeriesCalendrierDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("info");
  const [isAddHolidayModalOpen, setIsAddHolidayModalOpen] = useState(false);
  const [isRemoveHolidayDialogOpen, setIsRemoveHolidayDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [selectedHolidaysToAdd, setSelectedHolidaysToAdd] = useState([]);

  const calendarQ = useQuery({
    queryKey: ['holiday-calendar', id],
    queryFn: async () => {
      try {
        const r = await apiClient.get(`v1/holidays/holiday-calendars/${id}`);
        return r.data;
      } catch {
        navigate("/jours-feries/calendriers");
        throw new Error("Calendar not found");
      }
    },
  });

  const employeesQ = useQuery({
    queryKey: ['calendar-employees', id],
    queryFn: () =>
      apiClient.get(`v1/holidays/holiday-calendars/${id}/employees`)
        .then(r => Array.isArray(r.data) ? r.data : (r.data.employees || []))
        .catch(() => []),
    enabled: activeTab === 'employees',
  });

  const calendar = calendarQ.data ?? null;
  const holidays = calendar?.holidays ?? [];

  const availableQ = useQuery({
    queryKey: ['available-holidays-for-calendar', calendar?.year],
    queryFn: async () => {
      const r = await apiClient.get("v1/holidays/public-holidays", {
        params: { year: calendar.year, limit: 500 },
      });
      const currentIds = new Set(holidays.map(h => h.id));
      return (r.data.holidays || []).filter(h => !currentIds.has(h.id));
    },
    enabled: isAddHolidayModalOpen && !!calendar,
  });

  const employees = employeesQ.data ?? [];
  const availableHolidays = availableQ.data ?? [];
  const loading = calendarQ.isPending;

  const invalidateCalendar = () => queryClient.invalidateQueries({ queryKey: ['holiday-calendar', id] });

  const handleAddHolidays = async () => {
    if (selectedHolidaysToAdd.length === 0) {
      toast.error(t("holidayCalendarComponents.pageDetail.toasts.selectHoliday"));
      return;
    }
    try {
      await apiClient.post(`v1/holidays/holiday-calendars/${id}/holidays/batch`, {
        holiday_ids: selectedHolidaysToAdd, is_optional: false,
      });
      toast.success(t("holidayCalendarComponents.pageDetail.toasts.added", { count: selectedHolidaysToAdd.length }));
      setIsAddHolidayModalOpen(false);
      setSelectedHolidaysToAdd([]);
      invalidateCalendar();
      queryClient.invalidateQueries({ queryKey: ['available-holidays-for-calendar', calendar?.year] });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendarComponents.pageDetail.toasts.addError"));
    }
  };

  const handleRemoveHoliday = async () => {
    try {
      await apiClient.delete(`v1/holidays/holiday-calendars/${id}/holidays/${selectedHoliday.id}`);
      toast.success(t("holidayCalendarComponents.pageDetail.toasts.removed"));
      setIsRemoveHolidayDialogOpen(false);
      setSelectedHoliday(null);
      invalidateCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendarComponents.pageDetail.toasts.removeError"));
    }
  };

  const confirmRemoveHoliday = (holiday) => {
    setSelectedHoliday(holiday);
    setIsRemoveHolidayDialogOpen(true);
  };

  const handleApproveCalendar = async () => {
    try {
      await apiClient.post(`v1/holidays/holiday-calendars/${id}/approve`);
      toast.success(t("holidayCalendarComponents.pageDetail.toasts.approved"));
      invalidateCalendar();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayCalendarComponents.pageDetail.toasts.approveError"));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!calendar) return null;

  const statusConfig = STATUS_CONFIG[calendar.status] || STATUS_CONFIG.DRAFT;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/jours-feries/calendriers")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{calendar.name}</h1>
            <p className="text-muted-foreground">{calendar.country} • {calendar.year}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusConfig.className}>{t(`holidayCalendarComponents.status.${calendar.status}`, { defaultValue: calendar.status })}</Badge>
          {calendar.is_template && <Badge variant="outline">{t("holidayCalendarComponents.table.template")}</Badge>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="info" className="flex items-center gap-2">
            <Info className="h-4 w-4" />{t("holidayCalendarComponents.pageDetail.tabs.info")}
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />{t("holidayCalendarComponents.pageDetail.tabs.holidays", { count: holidays.length })}
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="h-4 w-4" />{t("holidayCalendarComponents.pageDetail.tabs.employees", { count: calendar.employees_count || 0 })}
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" />{t("holidayCalendarComponents.pageDetail.tabs.calendar")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <CalendrierDetailInfoTab calendar={calendar} handleApproveCalendar={handleApproveCalendar} />
        </TabsContent>

        <TabsContent value="holidays" className="mt-6">
          <CalendrierDetailHolidaysTab
            holidays={holidays}
            calendarStatus={calendar.status}
            setIsAddHolidayModalOpen={setIsAddHolidayModalOpen}
            confirmRemoveHoliday={confirmRemoveHoliday}
          />
        </TabsContent>

        <TabsContent value="employees" className="mt-6">
          <CalendrierDetailEmployeesTab
            employees={employees}
            calendarStatus={calendar.status}
            onNavigateToAffectations={() => navigate("/jours-feries/affectations")}
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <CalendrierDetailCalendarView calendar={calendar} holidays={holidays} />
        </TabsContent>
      </Tabs>

      <CalendrierDetailAddHolidayDialog
        isOpen={isAddHolidayModalOpen}
        onClose={setIsAddHolidayModalOpen}
        calendarName={calendar.name}
        calendarYear={calendar.year}
        availableHolidays={availableHolidays}
        selectedHolidaysToAdd={selectedHolidaysToAdd}
        setSelectedHolidaysToAdd={setSelectedHolidaysToAdd}
        handleAddHolidays={handleAddHolidays}
      />

      <CalendrierDetailRemoveDialog
        isOpen={isRemoveHolidayDialogOpen}
        setIsOpen={setIsRemoveHolidayDialogOpen}
        selectedHoliday={selectedHoliday}
        handleRemoveHoliday={handleRemoveHoliday}
      />
    </div>
  );
};

export default JoursFeriesCalendrierDetail;
